import React from 'react';
import { Card, Descriptions, Tag, Button, message, Modal, Form, Input, Space, Switch, TimePicker, Alert, Divider } from 'antd';
import { UserOutlined, MailOutlined, IdcardOutlined, EditOutlined, BankOutlined, PhoneOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

import { useAuthStore } from '../store/authStore';
import { usersProfileAPI, authAPI, pushAPI } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';
import PendingOrderCorrectionsCard from '../components/PendingOrderCorrectionsCard';
import { roleLabel } from '../utils/labels';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuthStore();
  const isMobile = useIsMobile();
  const [editModalVisible, setEditModalVisible] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form] = Form.useForm();
  const [pwdModalVisible, setPwdModalVisible] = React.useState(false);
  const [pwdSaving, setPwdSaving] = React.useState(false);
  const [pwdForm] = Form.useForm();

  const [pushLoading, setPushLoading] = React.useState(false);
  const [pushEnabled, setPushEnabled] = React.useState(false);
  const [pushTime, setPushTime] = React.useState<Dayjs | null>(dayjs('1970-01-01T10:30:00'));

  const swReady = typeof window !== 'undefined' && 'serviceWorker' in navigator;
  const pushSupported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    swReady &&
    'PushManager' in window;

  React.useEffect(() => {
    const loadPush = async () => {
      if (!user || user.role !== 'employee') return;
      try {
        const s = await pushAPI.getSettings();
        setPushEnabled(!!s.enabled);
        const t = s.time;
        setPushTime(t && /^\d{1,2}:\d{2}$/.test(t) ? dayjs(`1970-01-01T${t}:00`) : dayjs('1970-01-01T10:30:00'));
      } catch {
        // ignore
      }
    };
    loadPush();
  }, [user?.role]);

  if (!user) return null;

  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
    return outputArray;
  };

  const ensureSubscribed = async () => {
    if (!pushSupported) throw new Error('Push не поддерживается');
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') throw new Error('Разрешение на уведомления не выдано');

    const { publicKey } = await pushAPI.getVapidPublicKey();
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      }));

    await pushAPI.subscribe(sub.toJSON());
    return sub;
  };

  const disableSubscription = async () => {
    if (!pushSupported) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    try {
      await pushAPI.unsubscribe(sub.endpoint);
    } catch {
      // ignore
    }
    try {
      await sub.unsubscribe();
    } catch {
      // ignore
    }
  };

  const savePushSettings = async (nextEnabled: boolean, nextTime: Dayjs | null) => {
    setPushLoading(true);
    try {
      const timeStr = nextTime ? nextTime.format('HH:mm') : '10:30';

      if (nextEnabled) {
        await ensureSubscribed();
      } else {
        await disableSubscription();
      }

      const saved = await pushAPI.updateSettings({ enabled: nextEnabled, time: timeStr });
      setPushEnabled(saved.enabled);
      setPushTime(dayjs(`1970-01-01T${saved.time}:00`));
      message.success('Настройки напоминаний сохранены');
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Не удалось сохранить настройки');
      setPushEnabled(false);
    } finally {
      setPushLoading(false);
    }
  };

  const getRoleText = (role: string) => roleLabel(role);

  const openEdit = () => {
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      inn: user.inn ?? '',
      fullName: user.fullName ?? '',
      phone: user.phone ?? '',
      companyName: user.companyName ?? ''
    });
    setEditModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await usersProfileAPI.update({
        username: values.username,
        email: values.email,
        inn: values.inn || null,
        fullName: values.fullName || null,
        phone: values.phone || null,
        companyName: values.companyName || null
      });
      const fresh = await authAPI.me();
      updateUser({
        ...user,
        ...fresh
      });
      message.success('Профиль сохранён');
      setEditModalVisible(false);
    } catch (error: any) {
      if (error?.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error?.errorFields) {
        return;
      } else {
        message.error('Не удалось сохранить профиль');
      }
    } finally {
      setSaving(false);
    }
  };

  const showOrgFields = user.role === 'supplier' || user.role === 'customer' || user.role === 'customer_manager';

  return (
    <>
      <Card
        title={
          <span>
            <UserOutlined /> Профиль пользователя
          </span>
        }
        extra={
          <Space wrap>
            <Button
              onClick={() => {
                pwdForm.resetFields();
                setPwdModalVisible(true);
              }}
            >
              Сменить пароль
            </Button>
            <Button type="primary" icon={<EditOutlined />} onClick={openEdit}>
              Редактировать
            </Button>
          </Space>
        }
      >
        <Descriptions bordered column={1}>
          <Descriptions.Item label={<span><IdcardOutlined /> ID</span>}>{user.id}</Descriptions.Item>

          <Descriptions.Item label={<span><UserOutlined /> Имя пользователя</span>}>{user.username}</Descriptions.Item>

          <Descriptions.Item label={<span><MailOutlined /> Email</span>}>{user.email}</Descriptions.Item>

          {user.fullName && (
            <Descriptions.Item label="ФИО (контакт для партнёров)">{user.fullName}</Descriptions.Item>
          )}

          {user.phone && (
            <Descriptions.Item label={<span><PhoneOutlined /> Телефон</span>}>{user.phone}</Descriptions.Item>
          )}

          {user.companyName && (
            <Descriptions.Item label="Название компании">{user.companyName}</Descriptions.Item>
          )}

          <Descriptions.Item label="Роль">
            <Tag color="blue">{getRoleText(user.role)}</Tag>
          </Descriptions.Item>

          {user.inn && (
            <Descriptions.Item label={<span><BankOutlined /> ИНН</span>}>{user.inn}</Descriptions.Item>
          )}

          {user.customerId && (
            <Descriptions.Item label="ID Заказчика">{user.customerId}</Descriptions.Item>
          )}

          {user.supplierId && (
            <Descriptions.Item label="ID Поставщика">{user.supplierId}</Descriptions.Item>
          )}
        </Descriptions>

        {user.role === 'supplier' && (
          <p style={{ marginTop: 16, color: '#666', fontSize: 13 }}>
            Для заказчиков в разделе «Мои поставщики» отображаются ФИО, телефон и название компании из этого профиля
            (кнопка «Редактировать»).
          </p>
        )}
      </Card>

      <PendingOrderCorrectionsCard
        enabled={user.role === 'customer_manager' || user.role === 'customer'}
      />

      <Modal
        title="Сменить пароль"
        open={pwdModalVisible}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={pwdSaving}
        width={isMobile ? '100%' : 480}
        centered={!isMobile}
        onOk={async () => {
          try {
            const values = await pwdForm.validateFields();
            setPwdSaving(true);
            await authAPI.changePassword(values.currentPassword, values.newPassword);
            message.success('Пароль изменён');
            setPwdModalVisible(false);
          } catch (e: any) {
            if (e?.errorFields) return;
            message.error(e?.response?.data?.error || e?.message || 'Не удалось сменить пароль');
          } finally {
            setPwdSaving(false);
          }
        }}
        onCancel={() => setPwdModalVisible(false)}
        destroyOnClose
      >
        <Form form={pwdForm} layout="vertical">
          <Form.Item
            name="currentPassword"
            label="Текущий пароль"
            rules={[{ required: true, message: 'Введите текущий пароль' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>

          <Divider style={{ margin: '12px 0' }} />

          <Form.Item
            name="newPassword"
            label="Новый пароль"
            rules={[
              { required: true, message: 'Введите новый пароль' },
              { min: 6, message: 'Минимум 6 символов' }
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Повторите новый пароль"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Повторите новый пароль' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const p = getFieldValue('newPassword');
                  if (!value || value === p) return Promise.resolve();
                  return Promise.reject(new Error('Пароли не совпадают'));
                }
              })
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      {user.role === 'employee' && (
        <Card style={{ marginTop: 16 }} title="Напоминания (Web Push)">
          {!pushSupported ? (
            <Alert
              type="warning"
              showIcon
              message="Push-уведомления не поддерживаются"
              description="Откройте сайт в современном браузере (Chrome/Edge) и разрешите уведомления."
            />
          ) : (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Space wrap>
                <Switch
                  checked={pushEnabled}
                  loading={pushLoading}
                  onChange={(checked) => savePushSettings(checked, pushTime)}
                />
                <span>Напоминать заказать обед</span>
              </Space>

              <Space wrap>
                <span style={{ minWidth: 120 }}>Время:</span>
                <TimePicker
                  value={pushTime}
                  onChange={(v) => setPushTime(v)}
                  format="HH:mm"
                  minuteStep={5}
                  disabled={!pushEnabled || pushLoading}
                />
                <Button
                  onClick={() => savePushSettings(pushEnabled, pushTime)}
                  disabled={!pushEnabled || pushLoading}
                >
                  Сохранить время
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await ensureSubscribed();
                      const r = await pushAPI.test();
                      message.success(`Тест отправлен: ${r.sent}/${r.total}`);
                    } catch (e: any) {
                      message.error(e?.response?.data?.error || e?.message || 'Не удалось отправить тест');
                    }
                  }}
                  disabled={!pushEnabled || pushLoading}
                >
                  Тест
                </Button>
              </Space>
            </Space>
          )}
        </Card>
      )}

      <Modal
        title="Редактировать профиль"
        open={editModalVisible}
        onOk={handleSave}
        onCancel={() => setEditModalVisible(false)}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={saving}
        destroyOnClose
        width={isMobile ? '100%' : 520}
        centered={!isMobile}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="username"
            label="Имя пользователя"
            rules={[{ required: true, message: 'Введите имя пользователя' }, { min: 3, message: 'Минимум 3 символа' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, message: 'Введите email' }, { type: 'email', message: 'Некорректный email' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="fullName" label="ФИО контактного лица" tooltip="Видно заказчикам в «Мои поставщики»">
            <Input placeholder="Иванов Иван Иванович" />
          </Form.Item>

          <Form.Item name="phone" label="Телефон для связи">
            <Input placeholder="+7 999 123-45-67" />
          </Form.Item>

          {showOrgFields && (
            <Form.Item
              name="companyName"
              label="Название организации"
              rules={user.role === 'supplier' ? [{ required: true, message: 'Укажите название компании' }] : undefined}
            >
              <Input placeholder="ООО …" />
            </Form.Item>
          )}

          <Form.Item
            name="inn"
            label="ИНН"
            rules={[{ pattern: /^\d{10}$|^\d{12}$/, message: 'ИНН: 10 или 12 цифр' }]}
          >
            <Input placeholder="10 или 12 цифр" allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Profile;
